import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { createSelector } from 'reselect';
import type { ColumnField } from '../../server/types';
import type { ColumnConfig } from '../../types';
import Store from './Store';
import type { EntityState, NormalizedColumnConfig, TableState } from './types';
const {
  useSelector
} = Store;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useTableStoreSelector(selector) {
  return useSelector(selector);
}
function selectConfig<T extends Record<string, unknown>>(state: TableState<T>) {
  return state.config;
}
const selectConfigColumns = createSelector(selectConfig, config => config.columns);
function selectData<T extends Record<string, unknown>>(state: TableState<T>) {
  return state.data;
}
function selectEntities<T extends Record<string, unknown>>(state: EntityState<T>) {
  return state.entities;
}
type SelectConfigEntities = <T extends Record<string, unknown>>(state: TableState<T>) => Record<string, NormalizedColumnConfig<T>>;
const selectConfigEntities = createSelector(selectConfigColumns, selectEntities) as SelectConfigEntities;
type SelectConfigColumn = <T extends Record<string, unknown>>(state: TableState<T>, field: ColumnField<T>) => NormalizedColumnConfig<T> | undefined;
const selectConfigColumn = createSelector([selectConfigEntities, <T extends Record<string, unknown>,>(_: TableState<T>, field: ColumnField<T>) => field], (entities, field) => entities[field]) as SelectConfigColumn;
type SelectDataEntities = <T extends Record<string, unknown>>(state: TableState<T>) => Record<string, T>;
const selectDataEntities = createSelector(selectData, selectEntities) as unknown as SelectDataEntities;
type SelectDataRecord = <T extends Record<string, unknown>>(state: TableState<T>, id: string) => T;
const selectDataRecord = createSelector([selectDataEntities, <T extends Record<string, unknown>,>(_: TableState<T>, id: string) => id], (entities, id) => entities[id]!) as SelectDataRecord;
type SelectDataCellValue = <T extends Record<string, unknown>, K extends ColumnField<T>>(state: TableState<T>, id: string, column: ColumnField<T>) => T[Extract<K, string>] | null;
const selectDataCellValue = createSelector([selectDataEntities, <T extends Record<string, unknown>, K extends ColumnField<T>>(_: TableState<T>, id: string, column: K) => ({
  column,
  id
})], (entities, {
  id,
  column
}) => {
  const entity = entities[id];
  if (!entity) return null;
  const value = entity[column];
  return value;
}) as SelectDataCellValue;
// ) as SelectDataCellValue

type SelectIdx = <T extends Record<string, unknown>>(state: TableState<T>) => string[];
const selectConfigIdx = createSelector(selectConfigColumns, columns => columns.idx) as SelectIdx;
const selectDataIdx = createSelector(selectData, data => data.idx) as SelectIdx;
type SelectIsPivot = <T extends Record<string, unknown>>(state: TableState<T>) => boolean;
const selectIsPivot = createSelector(selectConfig, config => !!config.pivot) as SelectIsPivot;
type SelectFieldConfigList = <T extends Record<string, unknown>>(state: TableState<T>) => NormalizedColumnConfig<T>[];
const selectFieldConfigList = createSelector([selectConfigIdx, selectConfigEntities], (idx, entities) => idx.map(id => entities[id])) as SelectFieldConfigList;
export function useFieldConfigList() {
  const fields = useTableStoreSelector(selectFieldConfigList);
  return fields as Array<ColumnConfig<T>>;
}
type SelectTableId = <T extends Record<string, unknown>>(state: TableState<T>) => string;
const selectTableId = createSelector(selectConfig, config => config.id) as SelectTableId;
export function useTableId() {
  const id = useTableStoreSelector(selectTableId);
  return id;
}
export function useFields() {
  const fields = useTableStoreSelector(selectConfigIdx);
  return fields;
}
export function useDataIds() {
  const ids = useTableStoreSelector(selectDataIdx);
  return ids;
}
export function useIsPivot() {
  const isPivot = useTableStoreSelector(selectIsPivot);
  return isPivot;
}
export function useField(field) {
  const $ = _c(2);
  let t0;
  if ($[0] !== field) {
    t0 = state => selectConfigColumn(state, field);
    $[0] = field;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selector = t0;
  const column = useTableStoreSelector(selector);
  return column;
}
export function useConfigEntities() {
  const selector = _temp;
  const entities = useTableStoreSelector(selector);
  return entities;
}
function _temp(state) {
  return selectConfigEntities(state);
}
export function useData(recordId) {
  const $ = _c(2);
  let t0;
  if ($[0] !== recordId) {
    t0 = state => selectDataRecord(state, recordId);
    $[0] = recordId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selector = t0;
  const data = useTableStoreSelector(selector);
  return data;
}
export function useRows() {
  const fields = useFields();
  const dataIds = useDataIds();
  const pivot = useIsPivot();
  const rows = pivot ? fields : dataIds;
  return rows;
}
type SelectExpandable = <T extends Record<string, unknown>>(state: TableState<T>) => boolean;
const selectExpandable = createSelector(selectConfig, config => !!config.expandable) as SelectExpandable;
export function useExpandable() {
  const expandable = useTableStoreSelector(selectExpandable);
  return expandable;
}
export const PIVOTED_LABEL_COLUMN = 'label';
type SelectPivotColumns = <T extends Record<string, unknown>>(state: TableState<T>) => string[];
const selectPivotColumns = createSelector([selectConfigIdx, selectDataIdx, selectIsPivot], (fields, dataIds, isPivot) => {
  if (!isPivot) return fields;
  return [PIVOTED_LABEL_COLUMN, ...dataIds];
}) as SelectPivotColumns;
export function useColumns() {
  const columns = useTableStoreSelector(selectPivotColumns);
  return columns;
}
function getPivotField<T extends Record<string, unknown>>(fields: Record<string, NormalizedColumnConfig<T>>): NormalizedColumnConfig<T> {
  let minWidth = 0;
  let maxWidth = Number.POSITIVE_INFINITY;
  let width = 0;
  let resizable = false;
  for (const key in fields) {
    const field = fields[key]!;
    width = Math.max(width, field.width);
    if (typeof field.minWidth === 'number') {
      minWidth = Math.max(minWidth, field.minWidth);
    }
    if (typeof field.maxWidth === 'number') {
      maxWidth = Math.min(maxWidth, field.maxWidth);
    }
    resizable = resizable || !!field.resizable;
  }
  return {
    label: 'column',
    width,
    minWidth: minWidth === Number.POSITIVE_INFINITY ? undefined : minWidth,
    maxWidth: maxWidth === -1 ? undefined : maxWidth,
    resizable,
    field: 'column'
  };
}
type SelectPivotColumn = <T extends Record<string, unknown>>(state: TableState<T>) => NormalizedColumnConfig<T>;
const selectPivotColumn = createSelector(selectConfigEntities, entities => {
  return getPivotField(entities);
}) as SelectPivotColumn;
const DefaultPivotLabelColumnWidth = {
  width: 100,
  resizable: false,
  lastResizable: undefined,
  maxWidth: undefined,
  minWidth: undefined,
  field: 'label',
  label: 'label'
};
const selectPivotLabelColumnConfig = createSelector([selectConfig, selectIsPivot], (config, isPivot) => {
  if (!isPivot) return;
  if (!('pivotLabelColumnConfig' in config)) return;
  return {
    ...DefaultPivotLabelColumnWidth,
    ...config.pivotLabelColumnConfig,
    resizable: false
  };
});
type SelectPivotEntities = <T extends Record<string, unknown>>(state: TableState<T>) => Record<string, NormalizedColumnConfig<T>>;
const selectPivotEntities = createSelector([selectConfigEntities, selectIsPivot, selectPivotColumns, selectPivotColumn, selectPivotLabelColumnConfig], (entities, isPivot, columns, pivotColumn, pivotLabelColumnConfig) => {
  if (!isPivot) return entities;
  return columns.reduce((acc, column) => {
    if (column === PIVOTED_LABEL_COLUMN) {
      acc[column] = pivotLabelColumnConfig ?? DefaultPivotLabelColumnWidth;
      return acc;
    }
    acc[column] = {
      ...pivotColumn,
      field: column,
      label: column
    };
    return acc;
  }, {} as Record<string, NormalizedColumnConfig<Record<string, unknown>>>);
}) as SelectPivotEntities;
export function usePivotEntities() {
  const entities = useTableStoreSelector(selectPivotEntities);
  return entities;
}
export function useDataColumnValue(field, dataId) {
  const $ = _c(3);
  let t0;
  if ($[0] !== dataId || $[1] !== field) {
    t0 = state => selectDataCellValue(state, dataId, field);
    $[0] = dataId;
    $[1] = field;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const selector = t0;
  const data = useTableStoreSelector(selector);
  return data as T[K];
}